import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlane,
  faCircleCheck,
  faDownload,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../config";
import { airlines } from "../data/airlines";
import { mockFlights } from "../data/mockFlights";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";

function BookingConfirmation() {
  const flight = mockFlights[1]; // Using the return flight as an example
  const airline = airlines[flight.operatorCode];
  const bookingReference = "AMT123456";

  // Hardcoded data for missing fields
  const extendedFlightData = {
    outbound: {
      departureAirport: "London Heathrow",
      arrivalAirport: "Paris Charles de Gaulle",
      flightNumber: "AF1234",
      bookingClass: "Economy",
    },
    return: {
      departureAirport: "Paris Charles de Gaulle",
      arrivalAirport: "London Heathrow",
      flightNumber: "AF1235",
      bookingClass: "Economy",
    },
    payment: {
      subtotal: flight.price - 50, // Example price breakdown
      taxes: 50,
      total: flight.price,
      customerName: "Josh Sparkes",
      paymentMethod: "Credit Card",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 md:px-40 lg:px-40">
        <div className="flex justify-between py-6 items-center border-b">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="text-green-500 text-2xl"
            />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-gray-600 mb-4">
            Here's your itinerary confirmation & receipt
          </p>
          <p className="text-lg font-semibold mb-2">
            Booking Reference: {bookingReference}
          </p>
        </div>
        <ProgressBar currentStep={3}/>

        {/* Itinerary */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <h2 className="text-xl font-bold mb-6">Your Itinerary</h2>

          {/* Trip Type */}
          <div className="mb-6">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
              {flight.return_date ? "Round Trip" : "One Way"}
            </span>
          </div>

          {/* Outbound Flight */}
          <div className="border-b pb-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-100">
                <img
                  src={airline.logo}
                  alt={airline.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="font-semibold">{airline.name}</p>
                <p className="text-sm text-gray-600">
                  Flight {extendedFlightData.outbound.flightNumber}
                </p>
              </div>
            </div>

            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm text-gray-500">{flight.outbound_date}</p>
                <p className="text-xl font-bold">{flight.outbound_time}</p>
                <p className="font-medium">
                  {extendedFlightData.outbound.departureAirport}
                </p>
                <p className="text-sm text-gray-600">{flight.origin}</p>
              </div>

              <div className="text-center px-4">
                <p className="text-sm text-gray-500">{flight.duration}</p>
                <div className="relative w-32 my-2">
                  <div className="border-t-2 border-gray-300 w-full absolute top-1/2"></div>
                  <FontAwesomeIcon
                    icon={faPlane}
                    className={`text-${config.primaryColor} absolute -top-2 right-0`}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {flight.stops || "Direct Flight"}
                </p>
              </div>

              <div className="text-right">
                <p className="text-sm text-gray-500">
                  {flight.outbound_arrival_date}
                </p>
                <p className="text-xl font-bold">
                  {flight.outbound_arrival_time}
                </p>
                <p className="font-medium">
                  {extendedFlightData.outbound.arrivalAirport}
                </p>
                <p className="text-sm text-gray-600">{flight.destination}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Class:</span>{" "}
                {extendedFlightData.outbound.bookingClass}
              </p>
            </div>
          </div>

          {/* Return Flight */}
          {flight.return_date && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-white rounded-lg p-2 flex items-center justify-center border border-gray-100">
                  <img
                    src={airline.logo}
                    alt={airline.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <p className="font-semibold">{airline.name}</p>
                  <p className="text-sm text-gray-600">
                    Flight {extendedFlightData.return.flightNumber}
                  </p>
                </div>
              </div>

              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm text-gray-500">{flight.return_date}</p>
                  <p className="text-xl font-bold">{flight.return_time}</p>
                  <p className="font-medium">
                    {extendedFlightData.return.departureAirport}
                  </p>
                  <p className="text-sm text-gray-600">{flight.destination}</p>
                </div>

                <div className="text-center px-4">
                  <p className="text-sm text-gray-500">
                    {flight.return_duration || flight.duration}
                  </p>
                  <div className="relative w-32 my-2">
                    <div className="border-t-2 border-gray-300 w-full absolute top-1/2"></div>
                    <FontAwesomeIcon
                      icon={faPlane}
                      className={`text-${config.primaryColor} absolute -top-2 right-0`}
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    {flight.return_stops || "Direct Flight"}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-sm text-gray-500">{flight.return_date}</p>
                  <p className="text-xl font-bold">
                    {flight.return_arrival_time}
                  </p>
                  <p className="font-medium">
                    {extendedFlightData.return.arrivalAirport}
                  </p>
                  <p className="text-sm text-gray-600">{flight.origin}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Class:</span>{" "}
                  {extendedFlightData.return.bookingClass}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Important Notice */}
        <div className="bg-blue-50 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <FontAwesomeIcon icon={faClock} className="text-blue-600" />
            <p className="font-semibold text-blue-800">
              Remember to arrive at least 2 hours before departure
            </p>
          </div>
          <p className="text-sm text-blue-600 ml-8">
            This allows time for check-in and security procedures
          </p>
        </div>

        {/* Payment Receipt */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-6">Payment Receipt</h2>

          <div className="mb-6">
            <p className="text-gray-600">Customer Name</p>
            <p className="font-semibold">
              {extendedFlightData.payment.customerName}
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Flights Subtotal</span>
              <span>£{extendedFlightData.payment.subtotal}.00</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Taxes & Fees</span>
              <span>£{extendedFlightData.payment.taxes}.00</span>
            </div>
            <div className="flex justify-between pt-4 border-t font-bold">
              <span>Total Amount Paid</span>
              <span>£{extendedFlightData.payment.total}.00</span>
            </div>
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">
                Paid by {extendedFlightData.payment.paymentMethod}
              </p>
            </div>
          <button
            className={`bg-${config.primaryColor} text-white px-6 py-3 rounded-full flex items-center justify-center mx-auto hover:bg-opacity-90 transition-colors`}
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download Boarding Passes
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BookingConfirmation;
